import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, LogIn, LogOut, AlertTriangle, Calendar, Bug } from "lucide-react";
import { useStaffAuth } from "../context/StaffAuthContext";
import { getTodayPortal } from "../api/academicCalendarPortalApi";
import {
  getTodayAttendance,
  checkIn as apiCheckIn,
  checkOut as apiCheckOut,
  getOfficeTimePortal,
} from "../api/staffAttendancePortalApi";
import { ExamWidget } from "../components/widgets/ExamWidget";
import { AcademicCalendarWidget } from "../components/widgets/AcademicCalendarWidget";
import { ApplyLeaveWidget } from "../components/widgets/ApplyLeaveWidget";
import { NoticeWidget } from "../components/widgets/NoticeWidget";

// Helper Functions
const formatTime12hr = (time24) => {
  if (!time24) return "--";
  const [h, m] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};

const todayString = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
};

const SchoolHome = () => {
  const navigate = useNavigate();
  const { staff, loading, hasPermission } = useStaffAuth();

  // Debug Panel Toggle
  const [showDebug, setShowDebug] = useState(false);

  // Calendar State
  const [todayData, setTodayData] = useState(null);

  // Attendance States
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [attendanceActionLoading, setAttendanceActionLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState("");
  const [widgetOfficeTime, setWidgetOfficeTime] = useState(null);
  const [widgetClock, setWidgetClock] = useState(new Date());

  // Real-time tracking console logs
  useEffect(() => {
    if (staff) {
      console.log("================ STAFF DEBUG METRICS ================");
      console.log("Staff Authenticated User Context Data:", staff);
      console.log("Has TIMETABLE_VIEW permission:", typeof hasPermission === "function" ? hasPermission("TIMETABLE_VIEW") : false);
      console.log("Has TIMETABLE_FULL permission:", typeof hasPermission === "function" ? hasPermission("TIMETABLE_FULL") : false);
      console.log("Has PAYROLL_MANAGEMENT_VIEW:", typeof hasPermission === "function" ? hasPermission("PAYROLL_MANAGEMENT_VIEW") : false);
      console.log("Current assigned permission array:", staff?.roleId?.permissions || staff?.permissions || "No permission array located");

      // ── MODULE DEBUG ──
      console.log("--- MODULE DEBUG ---");
      console.log("Raw roleId object:", staff?.roleId);
      const moduleArr = staff?.roleId?.modules || staff?.modules || [];
      console.log("Current assigned module array:", moduleArr.length ? moduleArr : "No module array located");
      console.log("Module codes:", moduleArr.map((m) => (typeof m === "string" ? m : m?.code)));
      console.log(
        "Has STAFF_MANAGEMENT module:",
        moduleArr.some((m) => (typeof m === "string" ? m : m?.code) === "STAFF_MANAGEMENT")
      );
      console.log("=====================================================");
    }
  }, [staff, hasPermission]);

  // Real-time clock ticking
  useEffect(() => {
    const timer = setInterval(() => setWidgetClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch today's calendar status
  useEffect(() => {
    const fetchToday = async () => {
      try {
        const data = await getTodayPortal();
        setTodayData(data);
      } catch (err) {
        // Silently fail banner
      }
    };
    if (staff) fetchToday();
  }, [staff]);

  // Fetch today's attendance logs and office time
  const fetchTodayAttendance = async () => {
    try {
      setAttendanceLoading(true);
      const data = await getTodayAttendance();
      setTodayAttendance(data);
    } catch (err) {
      setAttendanceError("Failed to load today's attendance");
      setTimeout(() => setAttendanceError(""), 3000);
    } finally {
      setAttendanceLoading(false);
    }
  };

  useEffect(() => {
    if (staff) {
      fetchTodayAttendance();
      getOfficeTimePortal()
        .then((data) => setWidgetOfficeTime(data?.officeTime || null))
        .catch(() => {});
    }
  }, [staff]);

  // Attendance Action Handlers
  const handleWidgetCheckIn = async () => {
    try {
      setAttendanceActionLoading(true);
      await apiCheckIn();
      await fetchTodayAttendance();
    } catch (err) {
      setAttendanceError(err?.response?.data?.message || "Check-in failed");
      setTimeout(() => setAttendanceError(""), 3000);
    } finally {
      setAttendanceActionLoading(false);
    }
  };

  const handleWidgetCheckOut = async () => {
    try {
      setAttendanceActionLoading(true);
      await apiCheckOut();
      await fetchTodayAttendance();
    } catch (err) {
      setAttendanceError(err?.response?.data?.message || "Check-out failed");
      setTimeout(() => setAttendanceError(""), 3000);
    } finally {
      setAttendanceActionLoading(false);
    }
  };

  if (loading || !staff) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  const widgetAttendance = todayAttendance?.attendance;
  const widgetIsHoliday =
    todayAttendance?.dayType === "HOLIDAY" || widgetAttendance?.status === "HOLIDAY";

  return (
    <div className="min-h-screen relative">

      {/* VISUAL DEBUG FLOATER */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end">
        {showDebug && (
          <div className="bg-slate-900 text-green-400 p-4 rounded-xl shadow-2xl mb-2 text-xs font-mono max-w-sm border border-slate-700 max-h-96 overflow-y-auto">
            <p className="font-bold border-b border-slate-700 pb-1 mb-2 text-white flex items-center gap-1">
              <Bug size={12} /> Portal Diagnosis Metrics
            </p>
            <div className="space-y-1">
              <p><span className="text-neutral-400 dark:text-neutral-500">Staff ID:</span> {staff?._id || "undefined"}</p>
              <p><span className="text-neutral-400 dark:text-neutral-500">Role Name:</span> {staff?.roleId?.roleName || "N/A"}</p>
              <p><span className="text-neutral-400 dark:text-neutral-500">TIMETABLE_VIEW:</span> <span className={hasPermission("TIMETABLE_VIEW") ? "text-green-400" : "text-red-400"}>{String(hasPermission("TIMETABLE_VIEW"))}</span></p>
              <p><span className="text-neutral-400 dark:text-neutral-500">TIMETABLE_FULL:</span> <span className={hasPermission("TIMETABLE_FULL") ? "text-green-400" : "text-red-400"}>{String(hasPermission("TIMETABLE_FULL"))}</span></p>
              <p><span className="text-neutral-400 dark:text-neutral-500">PAYROLL_VIEW:</span> <span className={hasPermission("PAYROLL_MANAGEMENT_VIEW") ? "text-green-400" : "text-red-400"}>{String(hasPermission("PAYROLL_MANAGEMENT_VIEW"))}</span></p>
              <p className="text-neutral-400 dark:text-neutral-500 mt-2 border-t border-slate-800 pt-1">Resolved Access Keys:</p>
              <pre className="text-[10px] bg-slate-950 p-2 rounded max-h-24 overflow-y-auto text-emerald-300">
                {JSON.stringify(staff?.roleId?.permissions || staff?.permissions || [], null, 2)}
              </pre>
            </div>
          </div>
        )}
        <button
          onClick={() => setShowDebug(!showDebug)}
          className="bg-slate-900 text-white p-2.5 rounded-full shadow-lg hover:bg-slate-800 transition-colors flex items-center justify-center border border-slate-700"
          title="Toggle System Runtime Debug Visibility"
        >
          <Bug size={18} className={showDebug ? "text-green-400" : "text-white"} />
        </button>
      </div>

      <div className="max-w-5xl mx-auto p-6 space-y-6">

        {/* GREETING HEADER */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-neutral-800 dark:text-neutral-100 tracking-tight">
            {getGreeting()}, {staff.name}!
          </h1>
          <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Here is your dashboard overview for today.
          </p>
        </div>

        {/* Holiday / Half Day notification banner */}
        {todayData?.isHoliday && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
            <span className="text-2xl">🎉</span>
            <div>
              <p className="font-semibold text-red-700">Today is a Holiday — {todayData.reason}</p>
              {todayData.notification && (
                <p className="text-sm text-danger-600 dark:text-danger-500">{todayData.notification}</p>
              )}
            </div>
          </div>
        )}

        {todayData?.isHalfDay && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3">
            <span className="text-2xl">⏰</span>
            <div>
              <p className="font-semibold text-yellow-700">Today is a Half Day — {todayData.reason}</p>
              {todayData.notification && (
                <p className="text-sm text-yellow-600">{todayData.notification}</p>
              )}
            </div>
          </div>
        )}

        {/* Dynamic Action Error Panel */}
        {attendanceError && (
          <div className="bg-red-100 text-danger-600 dark:text-danger-500 p-3 rounded-xl text-sm">{attendanceError}</div>
        )}

        {/* ATTENDANCE ACTIONS WIDGET */}
        <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-neutral-100 dark:border-neutral-800 p-5">
          {attendanceLoading ? (
            <p className="text-sm text-neutral-400 dark:text-neutral-500">Loading today's attendance…</p>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-xs font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-1">
                  {todayString()}
                </p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 flex items-center gap-1.5">
                  <Clock size={14} />
                  {widgetClock.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </p>
                {widgetOfficeTime && (
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                    Office hours: {formatTime12hr(widgetOfficeTime.startTime)} –{" "}
                    {formatTime12hr(widgetOfficeTime.endTime)}
                  </p>
                )}
              </div>

              <div className="flex flex-col items-start sm:items-end gap-2">
                {widgetIsHoliday ? (
                  <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2">
                    <Calendar size={16} />
                    Today is a holiday
                  </div>
                ) : !widgetAttendance?.checkInTime ? (
                  <button
                    onClick={handleWidgetCheckIn}
                    disabled={attendanceActionLoading}
                    className="bg-green-600 text-white px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 disabled:opacity-50"
                  >
                    <LogIn size={16} />
                    {attendanceActionLoading ? "Checking in…" : "Check In"}
                  </button>
                ) : !widgetAttendance?.checkOutTime ? (
                  <>
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">
                      Checked in at <span className="font-semibold">{formatTime12hr(widgetAttendance.checkInTime)}</span>
                    </p>
                    {widgetAttendance.isLate && (
                      <div className="bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-2">
                        <AlertTriangle size={13} />
                        {widgetAttendance.lateByMinutes} minutes late
                      </div>
                    )}
                    <button
                      onClick={handleWidgetCheckOut}
                      disabled={attendanceActionLoading}
                      className="bg-red-600 text-white px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 disabled:opacity-50"
                    >
                      <LogOut size={16} />
                      {attendanceActionLoading ? "Checking out…" : "Check Out"}
                    </button>
                  </>
                ) : (
                  <p className="text-sm text-neutral-600 dark:text-neutral-300">
                    In: <span className="font-semibold">{formatTime12hr(widgetAttendance.checkInTime)}</span> · Out:{" "}
                    <span className="font-semibold">{formatTime12hr(widgetAttendance.checkOutTime)}</span>
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* PERMISSION-BASED WIDGETS GRID */}
        <div className="grid md:grid-cols-2 gap-6">
          {hasPermission("EXAM_MANAGEMENT_VIEW") && <ExamWidget isPortal={true} />}
          {hasPermission("ACADEMIC_CALENDAR_VIEW") && <AcademicCalendarWidget isPortal={true} />}
          <ApplyLeaveWidget isPortal={true} />
          <NoticeWidget isPortal={true} />
        </div>

      </div>
    </div>
  );
};

export default SchoolHome;